import { Box, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { InfoIcon } from "@/assets";
import { PanelActions } from "@/components/pricePositioningPanel/PanelActions";
import { getSliderRange } from "@/helpers/indicativeMarketValuation";
import { getProsaWebMarketValue } from "@/helpers/prosaWeb";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { FactorAdjustmentCard } from "./FactorAdjustmentCard";
import type { IndicativeApproachProps } from "./IndicativeMarketValuationPanel";
import { IndicativeSubtotalCard } from "./IndicativeSubtotalCard";
import { IndicativeValuationBreakdown } from "./IndicativeValuationBreakdown";
import { IndicativeValuationMetrics } from "./IndicativeValuationMetrics";

export const LandValueAssessment = ({
  valuationState,
  onSaveFactor,
  isSaving = false,
}: IndicativeApproachProps): JSX.Element => {
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const { prosaWeb, propertyInformation } = valuationState;
  const plotArea = propertyInformation?.plotData.plotArea;
  const recommendedGroundValueFactor = prosaWeb?.comparable_sales_approach?.standard_ground_value;
  const standardGroundValueFactor = prosaWeb?.factors?.standard_ground_value ?? recommendedGroundValueFactor;
  const sliderGroundValueFactor = {
    ...recommendedGroundValueFactor,
    value: standardGroundValueFactor?.value ?? recommendedGroundValueFactor?.value,
  };
  const sliderRange = getSliderRange(sliderGroundValueFactor, {
    minKey: "value_min",
    maxKey: "value_max",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [standardGroundValue, setStandardGroundValue] = useState(sliderRange.value);
  const canAdjustGroundValue =
    typeof recommendedGroundValueFactor?.value_min === "number" &&
    Number.isFinite(recommendedGroundValueFactor.value_min) &&
    typeof recommendedGroundValueFactor?.value_max === "number" &&
    Number.isFinite(recommendedGroundValueFactor.value_max);
  const showOfficialGroundValueInfo = !canAdjustGroundValue;

  useEffect(() => {
    setStandardGroundValue(sliderRange.value);
  }, [sliderRange.value]);

  const calculatedLandValue =
    typeof plotArea === "number" && Number.isFinite(plotArea) ? plotArea * standardGroundValue : null;
  const landValue =
    getProsaWebMarketValue(prosaWeb, {
      objectType: ObjectTypeEnum.plotOfLand,
    }) ?? calculatedLandValue;

  return (
    <>
      <Stack>
        <Typography variant="h4">{t("description.titleLandValueAssessment")}</Typography>
        <Typography>{t("description.text")}</Typography>
      </Stack>

      <IndicativeValuationMetrics
        approach="landValue"
        objectType={ObjectTypeEnum.plotOfLand}
        valuation={prosaWeb}
        plotArea={plotArea}
      />

      {canAdjustGroundValue ? (
        <FactorAdjustmentCard
          header={t("statisticalBaseValueAdjustment")}
          min={sliderRange.min}
          max={sliderRange.max}
          value={standardGroundValue}
          step={1}
          roundValue
          recommendedValue={recommendedGroundValueFactor?.recommended_value}
          onChange={(_event, value) => {
            if (typeof value === "number") {
              setStandardGroundValue(value);
              setIsEditing(true);
            }
          }}
        />
      ) : null}

      <IndicativeSubtotalCard
        value={isEditing ? calculatedLandValue : landValue}
        label={t(isEditing ? "preliminaryCalculation" : "metrics.resultingLandValue")}
        roundToThousands
        dataCy="indicative-market-value-panel-land-value-result"
      />

      {isEditing && !showOfficialGroundValueInfo && (
        <PanelActions
          onStopEditing={() => {
            setStandardGroundValue(sliderRange.value);
            setIsEditing(false);
          }}
          onApply={async () => {
            if (onSaveFactor) {
              await onSaveFactor(standardGroundValue);
            }

            setIsEditing(false);
          }}
          isApplying={isSaving}
        />
      )}

      <IndicativeValuationBreakdown
        objectType={ObjectTypeEnum.plotOfLand}
        valuation={prosaWeb}
        subtotalValue={calculatedLandValue}
        plotArea={plotArea}
        blurValues={isEditing}
      />

      {showOfficialGroundValueInfo && (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "20px",
            backgroundColor: theme.semanticColors.surface.blueBg,
          })}
        >
          <InfoIcon sx={(theme) => ({ fontSize: "20px", color: theme.themeColors.schwarzgrau500 })} />
          <Typography>{t("officialGroundValueInfo")}</Typography>
        </Box>
      )}
    </>
  );
};
